import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { useRouter } from "@shared/hooks/router";
import { useLocalStorage } from "@shared/hooks/use-local-storage";
import { apiClient } from "@shared/lib/api-client";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { AIModel, GeneratedPrompt, PromptLanguage } from "../types";

export type BatchPromptResult = {
	imageId: string;
	prompt: string;
	originalPrompt?: string;
	error?: string;
};

export function useBatchImageToPrompt() {
	const [imageFiles, setImageFiles] = useState<File[]>([]);
	const [selectedModel, setSelectedModel] = useState<AIModel>("flux");
	const [promptLanguage, setPromptLanguage] = useState<PromptLanguage>("en");
	const [results, setResults] = useState<BatchPromptResult[]>([]);
	const [promptHistory, setPromptHistory] = useState<GeneratedPrompt[]>([]);
	const [processingStatus, setProcessingStatus] = useState<
		"idle" | "processing" | "completed" | "error"
	>("idle");
	const { savePrompt } = useLocalStorage();
	const { user } = useSession();
	const router = useRouter();
	const {
		getRemainingCredits,
		hasUnlimitedCredits,
		useCredit,
		isUsingCredit,
	} = useCredits();

	// Convert image to Base64
	const convertImageToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64String = reader.result as string;
				const base64 = base64String.split(",")[1];
				resolve(base64);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	// Handle image selection
	const handleImagesSelected = (files: File[]) => {
		setImageFiles(files);
	};

	// Remove a specific image
	const removeImage = (index: number) => {
		setImageFiles((prev) => prev.filter((_, i) => i !== index));
	};

	// Clear all images
	const clearImages = () => {
		setImageFiles([]);
	};

	// Generate prompt for a single image
	const generatePromptMutation = useMutation({
		mutationFn: async (requestData: {
			imageBase64: string;
			model: AIModel;
			language: PromptLanguage;
			imageId: string;
		}) => {
			const response = await apiClient["image-to-prompt"].$post({
				json: {
					imageBase64: requestData.imageBase64,
					model: requestData.model,
					language: requestData.language,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to generate prompt");
			}

			const data = await response.json();
			return {
				...data,
				imageId: requestData.imageId,
			};
		},
	});

	// Process all images in batch
	const processBatch = async () => {
		if (generatePromptMutation.isPending || isUsingCredit) return;

		// Check if user is authenticated
		if (!user) {
			const returnUrl = "/batch-image-to-prompt";
			router.push(
				`/auth/login?redirectTo=${encodeURIComponent(returnUrl)}`,
			);
			return;
		}

		// Check if there are any images to process
		if (imageFiles.length === 0) {
			toast.error("Please upload at least one image");
			return;
		}

		// Check for sufficient credits
		const isUnlimited = hasUnlimitedCredits("IMAGE_TO_TEXT");
		const remainingCredits = getRemainingCredits("IMAGE_TO_TEXT");

		if (!isUnlimited && remainingCredits < imageFiles.length) {
			toast.error(
				`You need ${imageFiles.length} credits but only have ${remainingCredits}`,
			);
			return;
		}

		// Reset results and set processing state
		setResults([]);
		setProcessingStatus("processing");

		try {
			// Process each image sequentially to avoid rate limiting
			const newResults: BatchPromptResult[] = [];
			const newHistoryItems: GeneratedPrompt[] = [];

			for (let i = 0; i < imageFiles.length; i++) {
				const file = imageFiles[i];
				const imageId = `image-${i}`;

				try {
					// Convert image to base64
					const base64 = await convertImageToBase64(file);

					// Call the API
					const result = await generatePromptMutation.mutateAsync({
						imageBase64: base64,
						model: selectedModel,
						language: promptLanguage,
						imageId,
					});

					// Save result
					const promptResult: BatchPromptResult = {
						imageId,
						prompt: result.prompt,
						originalPrompt: result.originalPrompt,
					};

					newResults.push(promptResult);

					// Save to IndexedDB
					await savePrompt(
						result.prompt,
						result.originalPrompt,
						base64,
						selectedModel,
					);

					// Add to history
					const historyItem: GeneratedPrompt = {
						model: selectedModel,
						prompt: result.prompt,
						originalPrompt: result.originalPrompt,
						language: promptLanguage,
						timestamp: new Date(),
					};

					newHistoryItems.push(historyItem);

					// Use a credit for each successful conversion
					await useCredit("IMAGE_TO_TEXT");

					// Update results as we go
					setResults([...newResults]);
				} catch (error) {
					console.error(`Error processing image ${i}:`, error);

					// Add error to results
					newResults.push({
						imageId,
						prompt: "",
						error:
							error instanceof Error
								? error.message
								: "Unknown error",
					});

					setResults([...newResults]);
				}
			}

			// Update history with all new items
			setPromptHistory((prev) => [...newHistoryItems, ...prev]);
			setProcessingStatus("completed");
		} catch (error) {
			console.error("Error processing batch:", error);
			toast.error("Failed to process images");
			setProcessingStatus("error");
		}
	};

	// Get remaining credits formatted (with special handling for unlimited)
	const getRemainingCreditsFormatted = () => {
		if (hasUnlimitedCredits("IMAGE_TO_TEXT")) {
			return "infinity"; // Special marker for infinite credits
		}
		return getRemainingCredits("IMAGE_TO_TEXT");
	};

	return {
		imageFiles,
		selectedModel,
		promptLanguage,
		results,
		promptHistory,
		processingStatus,
		usesRemaining: getRemainingCreditsFormatted(),
		hasUnlimitedCredits: hasUnlimitedCredits("IMAGE_TO_TEXT"),
		isPending: generatePromptMutation.isPending || isUsingCredit,
		handleImagesSelected,
		removeImage,
		clearImages,
		processBatch,
		setSelectedModel,
		setPromptLanguage,
		setPromptHistory,
	};
}
